import { Link, useParams } from 'react-router-dom'

import { useArcade } from '../api/hooks'
import GameShell from '../components/arcade/GameShell'
import Blocks from '../components/arcade/games/Blocks'
import Breakout from '../components/arcade/games/Breakout'
import Game2048 from '../components/arcade/games/Game2048'
import Memory from '../components/arcade/games/Memory'
import Minesweeper from '../components/arcade/games/Minesweeper'
import Runner from '../components/arcade/games/Runner'
import Simon from '../components/arcade/games/Simon'
import Snake from '../components/arcade/games/Snake'
import Tower from '../components/arcade/games/Tower'
import { Card, Spinner } from '../components/ui'

interface Entry {
  slug: string
  name: string
  emoji: string
  blurb: string
  /** Score is a count of moves or seconds, where lower is better. */
  lowerIsBetter?: boolean
}

export const ARCADE_GAMES: Entry[] = [
  { slug: 'snake', name: 'Snake', emoji: '🐍', blurb: 'Eat, grow, do not bite yourself.' },
  { slug: '2048', name: '2048', emoji: '🔢', blurb: 'Slide tiles together and keep going.' },
  { slug: 'blocks', name: 'Block puzzle', emoji: '🧱', blurb: 'Falling shapes, clear the lines.' },
  { slug: 'breakout', name: 'Breakout', emoji: '🧨', blurb: 'Paddle, ball, wall of bricks.' },
  { slug: 'memory', name: 'Memory', emoji: '🃏', blurb: 'Find every pair in as few moves as you can.' },
  { slug: 'minesweeper', name: 'Minesweeper', emoji: '💣', blurb: 'Pure logic. No luck after the first click.' },
  { slug: 'simon', name: 'Simon', emoji: '🎵', blurb: 'Watch the sequence, repeat it back.' },
  { slug: 'runner', name: 'Endless runner', emoji: '🏃', blurb: 'One button. It only gets faster.' },
  { slug: 'tower', name: 'Tower builder', emoji: '🗼', blurb: 'Stack blocks. Sloppy drops narrow the tower.' },
]

export default function Arcade() {
  const { data: arcade, isPending } = useArcade()

  if (isPending) return <Spinner />

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Arcade</h1>
          <p className="text-sm text-muted">
            Nothing to do with languages. That is the point — you earned it.
          </p>
        </div>
        <Card className="px-4 py-2">
          <p className="text-sm">
            <span className="text-xl">🪙</span>{' '}
            <span className="font-semibold tabular-nums">{arcade?.balance ?? 0}</span>{' '}
            <span className="text-muted">tokens</span>
          </p>
        </Card>
      </div>

      <Card className="text-sm text-muted">
        Earn tokens by adding words, finishing lessons and keeping a streak.
        Each game costs {arcade?.play_cost ?? 10} — but your first go at every
        one is free.
      </Card>

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ARCADE_GAMES.map((game) => {
          const stats = arcade?.games.find((g) => g.game === game.slug)
          return (
            <li key={game.slug}>
              <Link to={`/arcade/${game.slug}`} className="block h-full">
                <Card className="h-full transition hover:border-brand">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl" aria-hidden>
                      {game.emoji}
                    </span>
                    <div className="min-w-0">
                      <h2 className="font-medium">{game.name}</h2>
                      <p className="text-sm text-muted">{game.blurb}</p>
                      <p className="mt-1 text-xs text-muted">
                        {stats?.plays
                          ? `Best ${stats.best_score} · ${stats.plays} plays`
                          : 'Not played yet'}
                        {stats?.free_play_available && ' · first go free'}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            </li>
          )
        })}

        <li>
          <Link to="/arcade/pixel-paint" className="block h-full">
            <Card className="h-full transition hover:border-brand">
              <div className="flex items-start gap-3">
                <span className="text-3xl" aria-hidden>
                  🎨
                </span>
                <div className="min-w-0">
                  <h2 className="font-medium">Pixel paint</h2>
                  <p className="text-sm text-muted">
                    A canvas with no score and no timer.
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {arcade?.pixel_paint_unlocked
                      ? 'Unlocked — free forever'
                      : `Unlock once for ${arcade?.pixel_paint_unlock_cost ?? 50}`}
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        </li>
      </ul>
    </div>
  )
}

/** Renders whichever arcade game the URL names. */
export function ArcadeGame() {
  const slug = useParams().game ?? ''
  const meta = ARCADE_GAMES.find((g) => g.slug === slug)

  if (!meta) {
    return (
      <div className="text-center">
        <p className="text-muted">No such game.</p>
        <Link to="/arcade" className="text-brand hover:underline">
          Back to the arcade
        </Link>
      </div>
    )
  }

  return (
    <GameShell
      game={meta.slug}
      title={meta.name}
      lowerIsBetter={meta.lowerIsBetter}
      instructions={<Instructions slug={meta.slug} />}
    >
      {({ onGameOver, onScore }) => {
        const props = { onGameOver, onScore }
        switch (meta.slug) {
          case 'snake':
            return <Snake {...props} />
          case '2048':
            return <Game2048 {...props} />
          case 'blocks':
            return <Blocks {...props} />
          case 'breakout':
            return <Breakout {...props} />
          case 'memory':
            return <Memory {...props} />
          case 'minesweeper':
            return <Minesweeper {...props} />
          case 'simon':
            return <Simon {...props} />
          case 'runner':
            return <Runner {...props} />
          case 'tower':
            return <Tower {...props} />
          default:
            return null
        }
      }}
    </GameShell>
  )
}

function Instructions({ slug }: { slug: string }) {
  const text: Record<string, string> = {
    snake: 'Arrow keys or WASD to steer. Eat the green square; do not hit the walls or yourself.',
    '2048': 'Arrow keys to slide every tile. Matching numbers merge. Keep going until nothing can move.',
    blocks: 'Left and right to move, up to rotate, down to hurry, space to drop. Fill a row to clear it.',
    breakout: 'Move the paddle with the mouse or arrow keys. Where the ball hits the paddle changes its angle.',
    memory: 'Turn over two cards at a time and find every pair. Fewer moves scores higher.',
    minesweeper: 'Click to open, right-click to flag. Your first click is always safe.',
    simon: 'Watch the pads light up, then repeat the sequence. It gets one longer every round.',
    runner: 'Space, tap or up to jump. It never stops getting faster.',
    tower: 'Tap or press space to drop the block. Whatever hangs over the edge is cut off.',
  }
  return <p>{text[slug] ?? ''}</p>
}
